// DAL — Vue "Consultation" ULTRA-LÉGÈRE (lecture seule).
//
// Rôles (V1.2) :
//   • "Assistant DAL" (wizard) = créer / modifier (saisie).
//   • "DAL" (ce composant)      = CONSULTER : aperçu du PDF + Télécharger + Modifier.
//
// Ne montre QUE :
//   1. un aperçu embarqué du PDF DAL du dossier :
//        - le PDF GÉNÉRÉ si `dal_genere` (GET .../dal/download, auth → blob → iframe),
//        - sinon le PDF IMPORTÉ (`dal_imported_pdf.document_id` → /api/metier/documents/{id}/download),
//        - sinon un empty-state.
//   2. un bouton « Télécharger ».
//   3. un bouton « Modifier » → bascule l'onglet DAL sur « Assistant DAL » via `onEdit`.
//
// Source unique : même endpoint que le wizard / DALForm → GET .../dal.
import { useState, useEffect, useCallback, useRef } from "react";
import { FileText, Download, Loader2, Pencil, FileWarning, Link2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// Auth session desktop (inchangée) — token + tenant depuis localStorage.
function getAuthHeaders() {
  const token = localStorage.getItem("session_token");
  const tenantId = localStorage.getItem("tenant_id");
  return {
    Authorization: `Bearer ${token}`,
    ...enteteTenant(tenantId),
  };
}

// Styles charte OmniHub (dual-mode via tokens var(--oh-*)). Boutons : hiérarchie
// stricte (primaire = accent plein ; secondaire = contour accent). États : paires
// sémantiques succès/erreur/info.
const btnPrimary = { background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" };
const btnSecondary = { background: "transparent", border: "1px solid var(--oh-accent)", color: "var(--oh-accent-text)" };
const infoStyle = { background: "var(--oh-info-bg)", color: "var(--oh-info-text)", borderColor: "var(--oh-info-border)" };
const successStyle = { background: "var(--oh-success-bg)", color: "var(--oh-success-text)", borderColor: "var(--oh-success-border)" };
const errorStyle = { background: "var(--oh-error-bg)", color: "var(--oh-error-text)", borderColor: "var(--oh-error-border)" };

export default function DALConsultation({ dossierId, reference, tenantId, client, infosLogement, onUpdate, onEdit }) {
  const [dal, setDal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);       // object URL (blob) pour l'iframe
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const objectUrlRef = useRef(null);
  // FT3 — « Lier DAL » : rattacher un PDF de DAL existant au dossier (archive,
  // pas de pré-remplissage — la consultation est en lecture seule).
  const linkFileRef = useRef(null);
  const [linking, setLinking] = useState(false);
  const [linkMsg, setLinkMsg] = useState(null); // { ok: bool, text: string }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal`, {
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      });
      if (res.ok) setDal(await res.json());
      else setDal({});
    } catch (e) { setDal({}); }
    finally { setLoading(false); }
  }, [dossierId]);

  useEffect(() => { load(); }, [load]);

  const d = dal || {};
  const dalGenere = !!d.dal_genere;
  const imported = d.dal_imported_pdf;
  const genereUrl = dalGenere ? (d.download_url || `/api/metier/tef/dossiers/${dossierId}/dal/download`) : null;
  const importedUrl = imported?.document_id ? `/api/metier/documents/${imported.document_id}/download` : null;
  // Priorité au PDF généré ; à défaut, le PDF importé.
  const activeUrl = genereUrl || importedUrl;
  const activeName = dalGenere
    ? (d.dal_pdf_filename || `DAL_${reference || dossierId}.pdf`)
    : (imported?.filename || imported?.nom || "DAL_importe.pdf");
  const hasPdf = !!activeUrl;

  // Récupère le PDF en blob (endpoint authentifié) → object URL pour l'aperçu.
  useEffect(() => {
    let cancelled = false;
    if (objectUrlRef.current) { window.URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = null; }
    setPdfUrl(null); setPdfError(null);
    if (!activeUrl) return;
    setPdfLoading(true);
    (async () => {
      try {
        const resp = await fetch(activeUrl.startsWith("http") ? activeUrl : `${API}${activeUrl}`, { headers: getAuthHeaders() });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        const url = window.URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setPdfUrl(url);
      } catch (e) {
        if (!cancelled) setPdfError(e?.message || "réseau");
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeUrl]);

  // Révoque l'object URL au démontage.
  useEffect(() => () => { if (objectUrlRef.current) window.URL.revokeObjectURL(objectUrlRef.current); }, []);

  const download = async () => {
    if (!activeUrl) return;
    try {
      const resp = await fetch(activeUrl.startsWith("http") ? activeUrl : `${API}${activeUrl}`, { headers: getAuthHeaders() });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href; a.download = activeName; a.click();
      window.URL.revokeObjectURL(href);
    } catch (e) { toast.error("Erreur téléchargement : " + (e?.message || e)); }
  };

  // FT3 — Lier un DAL existant : upload multipart vers l'endpoint import-pdf
  // (ARCHIVE/rattache le fichier). En consultation on ignore `extracted`/
  // `client_match` (lecture seule, pas de pré-remplissage), puis on recharge
  // pour afficher le PDF lié dans l'aperçu.
  const linkDal = async (e) => {
    const file = e.target.files?.[0];
    if (linkFileRef.current) linkFileRef.current.value = "";
    if (!file) return;
    setLinkMsg(null);
    if (!(file.type === "application/pdf" || /\.pdf$/i.test(file.name || ""))) {
      setLinkMsg({ ok: false, text: "Sélectionnez un fichier PDF." });
      return;
    }
    setLinking(true);
    try {
      const token = localStorage.getItem("session_token");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("nom", `DAL lié — ${file.name}`);
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/dal/import-pdf`, {
        method: "POST",
        headers: {
          ...enteteTenant(localStorage.getItem("tenant_id") || tenantId),
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        credentials: "include",
        body: fd,
      });
      if (res.ok) {
        setLinkMsg({ ok: true, text: `DAL lié : ${file.name}` });
        await load();          // recharge → le PDF lié apparaît dans l'aperçu
      } else {
        const j = await res.json().catch(() => ({}));
        setLinkMsg({ ok: false, text: j.detail || `Échec du rattachement (HTTP ${res.status}).` });
      }
    } catch (e2) {
      setLinkMsg({ ok: false, text: `Erreur réseau : ${e2?.message || e2}` });
    } finally {
      setLinking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm p-6" style={{ color: "var(--oh-text-secondary)" }} data-testid="dal-consultation-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement du DAL…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="dal-consultation">
      {/* Barre : titre + Télécharger + Modifier */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2"
           style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)" }}
           data-testid="dal-consult-actionbar">
        <div className="flex items-center gap-1.5 text-sm font-medium mr-2" style={{ color: "var(--oh-text)" }}>
          <FileText className="w-4 h-4" style={{ color: "var(--oh-accent-text)" }} /> DAL — {reference || dossierId}
        </div>
        <div className="flex-1" />
        <input ref={linkFileRef} type="file" accept="application/pdf,.pdf" onChange={linkDal} className="hidden" />
        <Button size="sm" variant="outline" disabled={linking} onClick={() => linkFileRef.current?.click()}
          className="disabled:opacity-40" style={btnSecondary} data-testid="dal-consult-lier">
          {linking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />} Lier DAL
        </Button>
        <Button size="sm" variant="outline" disabled={!hasPdf} onClick={download}
          className="disabled:opacity-40" style={btnSecondary} data-testid="dal-consult-download">
          <Download className="w-4 h-4 mr-1" /> Télécharger
        </Button>
        <Button size="sm" onClick={() => onEdit?.()} style={btnPrimary} data-testid="dal-consult-edit">
          <Pencil className="w-4 h-4 mr-1" /> Modifier
        </Button>
      </div>

      {/* FT3 — message de résultat du rattachement (charte : succès / erreur) */}
      {linkMsg && (
        <div className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
             style={linkMsg.ok ? successStyle : errorStyle}
             data-testid={linkMsg.ok ? "dal-consult-lier-success" : "dal-consult-lier-error"}>
          {linkMsg.ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
          {linkMsg.text}
        </div>
      )}

      {/* Aperçu du PDF */}
      {hasPdf ? (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--oh-border)" }} data-testid="dal-consult-pdf">
          {pdfLoading ? (
            <div className="flex items-center gap-2 text-sm p-10 justify-center" style={{ color: "var(--oh-text-secondary)" }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Chargement de l'aperçu…
            </div>
          ) : pdfError ? (
            <div className="p-6 text-center text-sm" data-testid="dal-consult-pdf-error">
              <div className="inline-flex items-center gap-2 rounded-lg border px-3 py-2" style={infoStyle}>
                <FileWarning className="w-4 h-4" /> Aperçu indisponible ({pdfError}). Utilisez « Télécharger ».
              </div>
            </div>
          ) : (
            <iframe title="Aperçu DAL" src={pdfUrl} className="w-full" style={{ height: "72vh", background: "#fff", border: 0 }} />
          )}
          <div className="px-3 py-1.5 text-[11px]" style={{ background: "var(--oh-bg-surface)", color: "var(--oh-text-secondary)" }}>
            {dalGenere ? "DAL généré" : "DAL importé"} — {activeName}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border p-10 text-center" style={{ borderColor: "var(--oh-border)", background: "var(--oh-bg-surface)" }} data-testid="dal-consult-empty">
          <FileText className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--oh-text-muted)" }} />
          <div className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>Aucun DAL généré ni importé</div>
          <div className="text-xs mt-1" style={{ color: "var(--oh-text-secondary)" }}>
            Cliquez sur « Modifier » pour ouvrir l'Assistant DAL et le compléter.
          </div>
        </div>
      )}
    </div>
  );
}
